import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { BeneficiaryData } from '@app/domain/model/policy/BeneficiaryData';
import { InsuredPersonBeneficiary } from '@app/domain/model/policy/InsuredPersonBeneficiary';
import { generateId } from '@app/lib/id-generator/generateId';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { Address } from '@app/domain/model/Address';
import { validateAddress } from './validateAddress';
import { percentageToShare } from './percentageToShare';
import { shareToPercentage } from './shareToPercentage';
import { BeneficiaryFormState } from '@app/domain/model/policy/BeneficiaryFormState';
import { parseFullName } from '@app/lib/parse-name/parseFullName';
import { parseEstonianPersonalCode } from '@app/lib/personal-code/parseEstonianPersonalCode';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';

interface BeneficiaryErrors {
    firstName: Nullable<ViolationError>;
    lastName: Nullable<ViolationError>;
    personalCode: Nullable<ViolationError>;
    share: Nullable<ViolationError>;
    address: Nullable<ViolationError>;
    email?: Nullable<ViolationError>;
    phone?: Nullable<ViolationError>;
}

interface BeneficiaryErrorsMap {
    [beneficiaryId: string]: BeneficiaryErrors;
}

export interface BeneficiaryFormValidation {
    isValid: boolean;
    totalShare: number;
    errors: BeneficiaryErrorsMap;
}

const SHARE_TARGET = 1;
const FLOAT_TOLERANCE = 1e-6;

const createEmptyBeneficiary = (): BeneficiaryData => ({
    id: generateId(),
    name: '',
    firstName: '',
    lastName: '',
    personalCode: '',
    share: 0,
    address: null,
    isNew: true,
});

const convertToBeneficiaryData = (beneficiary: InsuredPersonBeneficiary): BeneficiaryData => {
    const { firstName, lastName } = parseFullName(beneficiary.name ?? '');

    return {
        id: beneficiary.code || generateId(),
        name: beneficiary.name ?? '',
        firstName,
        lastName,
        personalCode: beneficiary.code || '',
        share: beneficiary.share || 0,
        address: null,
        isNew: false,
    };
};

export function useBeneficiaryFormState(
    initialNewBeneficiaries: BeneficiaryData[] = [],
    effectiveBeneficiaries: InsuredPersonBeneficiary[] = [],
    isEstonianEshop: boolean = false,
): BeneficiaryFormState {
    const [allBeneficiaries, setAllBeneficiaries] = useState<BeneficiaryData[]>(() => {
        const convertedExisting = effectiveBeneficiaries.map(convertToBeneficiaryData);
        return [...convertedExisting, ...initialNewBeneficiaries];
    });

    const convertedExisting = useMemo(
        () => effectiveBeneficiaries.map(convertToBeneficiaryData),
        [effectiveBeneficiaries],
    );

    useEffect(() => {
        setAllBeneficiaries(prevState => {
            const previousExistingBeneficiaries = prevState.filter(beneficiary => !beneficiary.isNew);
            const previousNewBeneficiaries = prevState.filter(beneficiary => beneficiary.isNew);

            const previousExistingById = new Map(previousExistingBeneficiaries.map(item => [item.id, item]));

            const mergedExistingBeneficiaries = convertedExisting.map(effectiveBeneficiary => {
                const localBeneficiary = previousExistingById.get(effectiveBeneficiary.id);
                return localBeneficiary
                    ? { ...effectiveBeneficiary, ...localBeneficiary, isNew: false }
                    : effectiveBeneficiary;
            });

            const hasMatchingIdsAndOrder =
                previousExistingBeneficiaries.length === mergedExistingBeneficiaries.length &&
                previousExistingBeneficiaries.every((item, index) => item.id === mergedExistingBeneficiaries[index].id);

            const hasContentChanged =
                !hasMatchingIdsAndOrder ||
                !mergedExistingBeneficiaries.every((merged, index) => {
                    const previous = previousExistingBeneficiaries[index];
                    if (!previous) return true;
                    return (
                        previous.name === merged.name &&
                        previous.firstName === merged.firstName &&
                        previous.lastName === merged.lastName &&
                        previous.personalCode === merged.personalCode &&
                        previous.share === merged.share
                    );
                });

            if (!hasContentChanged && initialNewBeneficiaries.length === 0) {
                return prevState;
            }

            const initialNewBeneficiariesDeduped = initialNewBeneficiaries.filter(
                newBeneficiary =>
                    !mergedExistingBeneficiaries.some(
                        existingBeneficiary => existingBeneficiary.id === newBeneficiary.id,
                    ),
            );

            return [...mergedExistingBeneficiaries, ...previousNewBeneficiaries, ...initialNewBeneficiariesDeduped];
        });
    }, [convertedExisting, initialNewBeneficiaries]);

    const newBeneficiaries = useMemo(
        () => allBeneficiaries.filter(beneficiary => beneficiary.isNew),
        [allBeneficiaries],
    );

    const existingBeneficiaries = useMemo(
        () => allBeneficiaries.filter(beneficiary => !beneficiary.isNew),
        [allBeneficiaries],
    );

    const validation: BeneficiaryFormValidation = useMemo(() => {
        const totalNewShare = newBeneficiaries.reduce((sum, beneficiary) => sum + (beneficiary.share || 0), 0);
        const errors: BeneficiaryErrorsMap = {};

        const findDuplicateBeneficiaries = (beneficiary: BeneficiaryData) => {
            if (newBeneficiaries.length === 0) return null;

            return newBeneficiaries.filter(
                oldBeneficiary => oldBeneficiary.personalCode === beneficiary.personalCode.trim(),
            );
        };

        for (let index = 0; index < newBeneficiaries.length; index++) {
            const beneficiary = newBeneficiaries[index];

            const firstNameError =
                beneficiary.firstName.trim() === '' ? { message: 'profile.validation.fieldRequired' } : null;
            const lastNameError =
                beneficiary.lastName.trim() === '' ? { message: 'profile.validation.fieldRequired' } : null;

            let personalCodeError: ViolationError | null = null;
            const beneficiaryPersonalCode = beneficiary.personalCode.trim();

            if (isEstonianEshop) {
                const personalCodeValidationResult = parseEstonianPersonalCode(beneficiaryPersonalCode);

                if (personalCodeValidationResult.isValid) {
                    personalCodeError = null;

                    const duplicateBeneficiaries = findDuplicateBeneficiaries(beneficiary);
                    if (duplicateBeneficiaries && duplicateBeneficiaries.length > 1) {
                        personalCodeError = { message: 'eshop.transport.validation.invalidPersonalCode' };
                    } else {
                        personalCodeError = null;
                    }
                } else {
                    personalCodeError = { message: 'eshop.transport.validation.invalidPersonalCode' };
                }
            }

            if (!isEstonianEshop) {
                const personalCodeValidationResult = parseLithuanianPersonalCode(beneficiaryPersonalCode);

                if (personalCodeValidationResult.isValid) {
                    personalCodeError = null;

                    const duplicateBeneficiaries = findDuplicateBeneficiaries(beneficiary);
                    if (duplicateBeneficiaries && duplicateBeneficiaries.length > 1) {
                        personalCodeError = { message: 'eshop.transport.validation.invalidPersonalCode' };
                    } else {
                        personalCodeError = null;
                    }
                } else {
                    personalCodeError = { message: 'eshop.transport.validation.invalidPersonalCode' };
                }
            }

            let shareError: Nullable<ViolationError> = null;
            if ((beneficiary.share ?? 0) > 1 + FLOAT_TOLERANCE) {
                shareError = { message: 'lifeInsurance.shareExceedsHundredPercent' };
            }

            let addressError: Nullable<ViolationError> = null;
            let emailError: Nullable<ViolationError> = null;
            let phoneError: Nullable<ViolationError> = null;

            if (isEstonianEshop) {
                const email = beneficiary.email?.trim();
                const phone = beneficiary.phone?.trim();

                if (!email) {
                    emailError = { message: 'eshop.life.validation.fieldRequired' };
                } else {
                    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
                    if (!emailRegex.test(email)) {
                        emailError = { message: 'profile.validation.invalidEmail' };
                    }
                }

                if (!phone) {
                    phoneError = { message: 'eshop.life.validation.fieldRequired' };
                }
            } else {
                addressError = validateAddress(beneficiary.address);
            }

            errors[beneficiary.id] = {
                firstName: firstNameError,
                lastName: lastNameError,
                personalCode: personalCodeError ?? null,
                share: shareError,
                address: addressError,
                email: emailError,
                phone: phoneError,
            };
        }

        const hasAnyErrors = Object.values(errors).some(error => {
            if (isEstonianEshop) {
                return (
                    error.firstName || error.lastName || error.personalCode || error.share || error.email || error.phone
                );
            } else {
                return error.firstName || error.lastName || error.personalCode || error.share || error.address;
            }
        });

        const withinMaxTotal = totalNewShare <= SHARE_TARGET + FLOAT_TOLERANCE;

        return {
            isValid: withinMaxTotal && !hasAnyErrors,
            totalShare: totalNewShare,
            errors,
        };
    }, [newBeneficiaries, isEstonianEshop]);

    const addBeneficiary = useCallback(
        (existingShareTotal: number = 0) => {
            const remaining = Math.max(0, SHARE_TARGET - validation.totalShare - existingShareTotal);
            const roundedTo05 = Math.round(remaining * 20) / 20;
            const initialShare = Math.max(0.1, roundedTo05);

            const created = createEmptyBeneficiary();
            created.share = initialShare;

            setAllBeneficiaries(prev => [...prev, created]);
        },
        [validation.totalShare],
    );

    const removeBeneficiary = useCallback((beneficiaryId: string) => {
        setAllBeneficiaries(previousBeneficiaries =>
            previousBeneficiaries.filter(beneficiary => beneficiary.id !== beneficiaryId),
        );
    }, []);

    const updateById = useCallback(
        (beneficiaryId: string, updater: (beneficiary: BeneficiaryData) => BeneficiaryData) => {
            setAllBeneficiaries(previousBeneficiaries =>
                previousBeneficiaries.map(item => (item.id === beneficiaryId ? updater(item) : item)),
            );
        },
        [],
    );

    const onFirstNameChange = useCallback(
        (beneficiaryId: string, e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, firstName: value }));
        },
        [updateById],
    );

    const onLastNameChange = useCallback(
        (beneficiaryId: string, e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, lastName: value }));
        },
        [updateById],
    );

    const onPersonalCodeChange = useCallback(
        (beneficiaryId: string, e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, personalCode: value }));
        },
        [updateById],
    );

    const onShareChange = useCallback(
        (beneficiaryId: string, percentageValue: string) => {
            const shareValue = percentageToShare(percentageValue);
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, share: shareValue }));
        },
        [updateById],
    );

    const onAddressChange = useCallback(
        (beneficiaryId: string, address: Nullable<Address>) => {
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, address }));
        },
        [updateById],
    );

    const onEmailChange = useCallback(
        (beneficiaryId: string, e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, email: value }));
        },
        [updateById],
    );

    const onPhoneChange = useCallback(
        (beneficiaryId: string, e: ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value.replace(/\D/g, '');

            if (value.length > 8) {
                value = value.slice(0, 8);
            }

            updateById(beneficiaryId, previousBeneficiary => ({ ...previousBeneficiary, phone: value }));
        },
        [updateById],
    );

    const resetBeneficiaries = useCallback(() => {
        setAllBeneficiaries(previousBeneficiaries => previousBeneficiaries.filter(beneficiary => !beneficiary.isNew));
    }, []);

    const setBeneficiariesFromData = useCallback((data: BeneficiaryData[]) => {
        setAllBeneficiaries(data);
    }, []);

    const isShareComplete = useMemo(
        () => Math.abs(validation.totalShare - SHARE_TARGET) < FLOAT_TOLERANCE,
        [validation.totalShare],
    );

    const canAddBeneficiary = useMemo(
        () => validation.totalShare <= SHARE_TARGET - 0.1 + FLOAT_TOLERANCE,
        [validation.totalShare],
    );

    const getBeneficiaryById = useCallback(
        (beneficiaryId: string) => allBeneficiaries.find(beneficiary => beneficiary.id === beneficiaryId) ?? null,
        [allBeneficiaries],
    );

    const getBeneficiaryPercentage = useCallback(
        (beneficiaryId: string): string => {
            const beneficiary = getBeneficiaryById(beneficiaryId);
            return beneficiary ? shareToPercentage(beneficiary.share) : '10';
        },
        [getBeneficiaryById],
    );

    return useMemo(
        () => ({
            beneficiaries: allBeneficiaries,
            newBeneficiaries,
            existingBeneficiaries,
            validation,
            isShareComplete,
            canAddBeneficiary,
            addBeneficiary,
            removeBeneficiary,
            onFirstNameChange,
            onLastNameChange,
            onPersonalCodeChange,
            onShareChange,
            onAddressChange,
            onEmailChange,
            onPhoneChange,
            getBeneficiaryById,
            getBeneficiaryPercentage,
            resetBeneficiaries,
            setBeneficiariesFromData,
        }),
        [
            allBeneficiaries,
            newBeneficiaries,
            existingBeneficiaries,
            validation,
            isShareComplete,
            canAddBeneficiary,
            addBeneficiary,
            removeBeneficiary,
            onFirstNameChange,
            onLastNameChange,
            onPersonalCodeChange,
            onShareChange,
            onAddressChange,
            onEmailChange,
            onPhoneChange,
            getBeneficiaryById,
            getBeneficiaryPercentage,
            resetBeneficiaries,
            setBeneficiariesFromData,
        ],
    );
}
